import { useEffect, useMemo, useState } from 'react';
import Avatar from '../components/Avatar';
import Modal, { ModalButton } from './Modal';
import { dmApi, displayName } from './format';

const MAX_MEMBERS = 10;
const NAME_MAX = 100;

// Searchable list of my contacts (people I already chat with) with checkboxes
function ContactPicker({ exclude = [], selected, onToggle, limit, nicknames }) {
  const [contacts, setContacts] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    dmApi.get('/api/dm/contacts').then(r => setContacts(r.data)).catch(() => setContacts([]));
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (contacts || [])
      .filter(c => !exclude.includes(c.id))
      .filter(c => !q || `${c.username} ${c.display_name || ''} ${nicknames[c.id] || ''}`.toLowerCase().includes(q));
  }, [contacts, query, exclude, nicknames]);

  if (contacts === null) return <div className="py-6 text-center text-sm text-text-dim">Loading…</div>;
  if (contacts.filter(c => !exclude.includes(c.id)).length === 0) {
    return <div className="py-6 text-center text-sm leading-relaxed text-text-dim">Nobody to add. You can add people once they have accepted a message request from you, or you from them.</div>;
  }
  return (
    <>
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search your contacts"
        className="mb-2 box-border w-full rounded-sm border border-solid border-border bg-surface-3 px-3 py-2 font-body text-sm text-text outline-none placeholder:text-text-dim focus:border-purple"
      />
      <div className="max-h-64 overflow-y-auto overscroll-contain">
        {visible.map(c => {
          const checked = selected.includes(c.id);
          const disabled = !checked && selected.length >= limit;
          return (
            <label key={c.id} className={`flex items-center gap-3 rounded-sm px-2 py-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-surface-3'}`}>
              <Avatar src={c.avatar} size={32} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display text-sm font-bold text-text">{displayName(c, nicknames)}</span>
                <span className="block truncate text-xs text-text-dim">@{c.username}</span>
              </span>
              <input type="checkbox" checked={checked} disabled={disabled} onChange={() => onToggle(c.id)} className="h-4 w-4 accent-[#9b5de5]" />
            </label>
          );
        })}
        {visible.length === 0 && <div className="py-4 text-center text-sm text-text-dim">No matches</div>}
      </div>
    </>
  );
}

const toggleIn = (list, id) => (list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

// New group: pick 2 to 9 contacts and an optional name
export function CreateGroupDialog({ nicknames, onClose, onCreated }) {
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await dmApi.post('/api/dm/groups', { member_ids: selected, name: name.trim() || null });
      onCreated(res.data.conversation_id);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the group.');
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New group"
      onClose={onClose}
      footer={<>
        <ModalButton onClick={onClose}>Cancel</ModalButton>
        <ModalButton primary disabled={selected.length < 2 || busy} onClick={create}>
          {busy ? 'Creating…' : 'Create group'}
        </ModalButton>
      </>}
    >
      <p className="mb-3 mt-0 text-[13px] leading-relaxed text-text-dim">
        Pick at least 2 people you already chat with. Groups have up to {MAX_MEMBERS} members. {selected.length} selected.
      </p>
      <input
        value={name}
        onChange={e => setName(e.target.value.slice(0, NAME_MAX))}
        placeholder="Group name (optional)"
        className="mb-3 box-border w-full rounded-sm border border-solid border-border bg-surface-3 px-3 py-2 font-body text-sm text-text outline-none placeholder:text-text-dim focus:border-purple"
      />
      <ContactPicker selected={selected} onToggle={id => setSelected(s => toggleIn(s, id))} limit={MAX_MEMBERS - 1} nicknames={nicknames} />
      {error && <div className="mt-2 text-xs text-red">{error}</div>}
    </Modal>
  );
}

// Add contacts to an existing group (any member)
export function AddMembersDialog({ conversation, nicknames, onClose }) {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const memberIds = conversation.members.map(m => m.id);
  const free = (conversation.max_members || MAX_MEMBERS) - memberIds.length;

  const add = async () => {
    setBusy(true);
    setError('');
    try {
      await dmApi.post(`/api/dm/groups/${conversation.id}/members`, { user_ids: selected });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add them.');
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Add people"
      onClose={onClose}
      footer={<>
        <ModalButton onClick={onClose}>Cancel</ModalButton>
        <ModalButton primary disabled={!selected.length || busy} onClick={add}>{busy ? 'Adding…' : 'Add'}</ModalButton>
      </>}
    >
      {free <= 0 ? (
        <p className="m-0 text-sm text-text-muted">This group is full ({MAX_MEMBERS} members).</p>
      ) : (
        <>
          <p className="mb-3 mt-0 text-[13px] leading-relaxed text-text-dim">
            New members can read the earlier messages in this group. {free} {free === 1 ? 'place' : 'places'} left.
          </p>
          <ContactPicker exclude={memberIds} selected={selected} onToggle={id => setSelected(s => toggleIn(s, id))} limit={free} nicknames={nicknames} />
        </>
      )}
      {error && <div className="mt-2 text-xs text-red">{error}</div>}
    </Modal>
  );
}
